import { useCallback, useRef, useState } from 'react';

import { useContentStudioApi } from '../../../common/hooks/use-content-studio-api.hook';
import { normalizeComposition, totalDuration, VideoComposition } from '../content-studio/video-editor/composition';
import { exportComposition } from '../content-studio/video-editor/export';
import { VideoExportState } from '../content-studio/video-editor/VideoEditorModal';
import { boardToComposition, filmSignature, hashString } from './film';
import { measureLegibility, measureLogo } from './legibility';
import { StudioBoard } from './studio-contract';

/**
 * The edit: from the board to a finished film.
 *
 * "Make the film" lays the shots out in order with their text and a music bed
 * (made once, from the mood Ardi wrote, and kept), then opens the timeline
 * editor on it. Whatever the vendor changes there is kept on the board, so
 * opening it again continues the same edit rather than rebuilding it — until
 * they ask to rebuild it from the board. A cut nobody has touched is rebuilt
 * on its own when the board has changed since (new captions, a new take).
 */
export function useFilm(opts: {
    projectUuid?: string;
    board: StudioBoard;
    setBoard: (u: (b: StudioBoard) => StudioBoard) => void;
    /** The vendor's brand: their logo and name stand in for the reference's. */
    brand?: { logoUrl?: string; name?: string };
}) {
    const api = useContentStudioApi();
    const [composition, setComposition] = useState<VideoComposition | null>(null);
    const [preparing, setPreparing] = useState(false);
    const [exportState, setExportState] = useState<VideoExportState | null>(null);
    const controller = useRef<AbortController | null>(null);
    const latest = useRef(opts);
    latest.current = opts;

    const open = useCallback(async (rebuild = false) => {
        const { board, setBoard } = latest.current;
        const saved = board.edit?.composition;
        const stale = !!board.edit?.builtFrom && board.edit.builtFrom !== filmSignature(board) && !board.edit.edited;
        if (saved && !rebuild && !stale) {
            setComposition(normalizeComposition(saved));
            return;
        }
        setPreparing(true);
        try {
            let musicUrl = board.edit?.musicUrl;
            const draft = boardToComposition(board, {});
            const seconds = Math.max(6, Math.ceil(totalDuration(draft)) + 1);
            if (!musicUrl && board.edit?.musicPrompt) {
                try {
                    const music = await api.generateMusic({ prompt: board.edit.musicPrompt, durationSec: Math.min(60, seconds) });
                    musicUrl = music.url;
                    setBoard((b) => ({ ...b, edit: { ...(b.edit || {}), musicUrl } }));
                } catch { /* a film without music is still a film */ }
            }
            const current = latest.current.board;
            // Bounded: a slow picture costs a deeper shadow, never the film.
            const within = <T,>(work: Promise<T>, fallback: T) => Promise.race([work.catch(() => fallback), new Promise<T>((r) => window.setTimeout(() => r(fallback), 8000))]);
            const [luma, logoLuma] = await Promise.all([within(measureLegibility(current), {} as Record<string, number>), within(measureLogo(latest.current.brand?.logoUrl), undefined)]);
            const built = normalizeComposition(boardToComposition(current, { musicUrl, brand: latest.current.brand, luma, logoLuma }));
            const builtHash = hashString(JSON.stringify(built));
            setBoard((b) => ({ ...b, edit: { ...(b.edit || {}), composition: built, builtFrom: filmSignature(current), builtHash, edited: false } }));
            setComposition(built);
        } finally {
            setPreparing(false);
        }
    }, [api]);

    const onChange = useCallback((comp: VideoComposition) => {
        // Once the cut differs from what was built, it is the vendor's edit and is never rebuilt behind their back.
        latest.current.setBoard((b) => {
            const edited = b.edit?.edited || (!!b.edit?.builtHash && hashString(JSON.stringify(normalizeComposition(comp))) !== b.edit.builtHash);
            return { ...b, edit: { ...(b.edit || {}), composition: comp, edited } };
        });
    }, []);

    const close = useCallback((comp: VideoComposition) => {
        onChange(comp);
        setComposition(null);
    }, [onChange]);

    const runExport = useCallback(async (comp: VideoComposition) => {
        const projectUuid = latest.current.projectUuid;
        if (!projectUuid || controller.current) return;
        const ctl = new AbortController();
        controller.current = ctl;
        setExportState({ status: 'running', progress: { phase: 'prepare', progress: 0 } });
        let last = 0;
        try {
            const result = await exportComposition(normalizeComposition(comp), {
                signal: ctl.signal,
                onProgress: (p) => {
                    const now = performance.now();
                    if (now - last < 150 && p.progress < 1) return;
                    last = now;
                    setExportState({ status: 'running', progress: { ...p, progress: p.progress * 0.92 } });
                },
            });
            setExportState({ status: 'running', progress: { phase: 'upload', progress: 0.94 } });
            const file = new File([result.blob], `studio-film.${result.extension}`, { type: result.mimeType });
            const asset = await api.importVideo(projectUuid, file, {
                thumbnail: result.thumbnail, width: result.width, height: result.height, durationSec: result.durationSec, origin: 'editor',
            });
            latest.current.setBoard((b) => ({ ...b, edit: { ...(b.edit || {}), composition: comp, renderedUrl: asset.url, renderedAssetUuid: asset.uuid } }));
            setExportState({ status: 'done', url: asset.url });
        } catch (err: any) {
            setExportState(err?.name === 'AbortError' || /cancel/i.test(err?.message || '')
                ? null
                : { status: 'error', error: err?.message || 'Export failed' });
        } finally {
            controller.current = null;
        }
    }, [api]);

    const cancelExport = useCallback(() => {
        controller.current?.abort();
        controller.current = null;
        setExportState(null);
    }, []);

    return { composition, preparing, open, close, onChange, exportState, runExport, cancelExport, dismissExport: () => setExportState(null) };
}
